import { IPoolHistory } from "../api/history-pool-data";
import { IConnect } from "../api/connect-data";
import { IPayouts } from "../api/payouts-data";
import {
  ICoin,
  ICoins,
  IFoundBlocks,
  IPoolLuck,
  IPoolStats,
  IPoolStatsHistory,
} from "../api/main-data";
import {
  IDomainCoinRatio,
  IDomainConnect,
  IDomainFoundBlock,
  IDomainPayout,
  IDomainPoolHistoryStat,
  IDomainPoolHistoryUserStat,
  IDomainPoolStat,
  IDomainPoolStatHistories,
  IDomainPoolStatHistory,
  IDomainUser,
  IDomainUserBalance,
  IDomainUserPoolStatHistories,
  IDomainUserPoolStatHistory,
  IDomainUserStatWorker,
  IDomainUserWorkerStatHistories,
  IDomainUserWorkerStatHistory,
} from "./data";
import {
  convertHashrateToFloatWithPrecision,
  convertHashrateToString,
  formatShareRate,
  formatShareRateValue,
  getCurrentTimeTimestamp,
  getHashrateSuffix,
  getMaxHashratePoolStats,
  getMaxHashrateUserStats,
  getMaxHashrateUserWorkerStats,
  numberWithWhiteSpaces,
  numberWithWhiteSpacesViaLocale,
} from "@/data-converters/metrics-pipe";
import { IUserData, IUserSettings, IUserSettingsCoin } from "../api/auth-data";
import {
  IPPLNSAccResponse,
  IUserBalance,
  IUserStats,
  IUserStatsHistory,
  IUserStatsWorker,
  IUserWorkerStatsHistory,
} from "../api/monitoring-data";
import { ICoinRatio } from "../api/profit-settings-data";
import { IFeePlans } from "../api/users-data";
import { AppConstants } from "@/app/constants";
import {
  formatUnixDateEnglish,
  formatUnixTimeEnglish,
  formatUnixTimeDateEnglish,
} from "@/app/date-format";

const SHARE_WORK_NORMALIZATION_DIVISOR_DEFAULT = 2 ** 32;
const SHARE_WORK_NORMALIZATION_DIVISOR_SCRYPT = 2 ** 16;

//---------- shared ----------

export function mapApiCoinsToDomain(
  apiResponse: ICoins
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  apiResponse.coins.forEach((c) => {
    if (result.has(c.algorithm)) {
      result.get(c.algorithm)?.push(c.name);
    } else {
      result.set(c.algorithm, [c.name]);
    }
  });
  return result;
}

export function mapApiToDomainCoins(apiCoins: ICoins): Map<string, string[]> {
  const result = new Map<string, string[]>();

  apiCoins.coins.forEach((c) => {
    if (result.has(c.algorithm)) {
      result.get(c.algorithm)?.push(c.name);
    } else {
      result.set(c.algorithm, [c.name]);
    }
  });

  result.forEach((valuesList, key) => {
    if (valuesList.length > 1 && !valuesList.includes(key)) {
      valuesList.push(key);
    }
  });
  applySort(result);

  return result;
}

export function mapApiToDomainCoinsWithDefaultCoin(
  apiCoins: ICoins
): Map<string, string[]> {
  const result = new Map<string, string[]>();

  apiCoins.coins.forEach((c) => {
    if (result.has(c.algorithm)) {
      result.get(c.algorithm)?.push(c.name);
    } else {
      result.set(c.algorithm, [c.name]);
    }
  });

  result.forEach((valuesList, key) => {
    if (valuesList.length > 1 && !valuesList.includes(key)) {
      valuesList.push(key);
    }
  });
  applySort(result);

  return result;
}

export function mapToDomainCoinsAsIs(response: ICoins): Map<string, string[]> {
  const result = new Map<string, string[]>();

  response.coins.forEach((c) => {
    if (result.has(c.algorithm)) {
      result.get(c.algorithm)?.push(c.name);
    } else {
      result.set(c.algorithm, [c.name]);
    }
  });

  applySort(result);

  return result;
}

export function mapCoinsInfo(response: ICoins): Map<string, ICoin> {
  const result = new Map<string, ICoin>();

  response.coins.forEach((c) => {
    result.set(c.name, c);
  });

  return result;
}

function applySort(coinsMap: Map<string, string[]>) {
  if (coinsMap.has("sha256")) {
    const bchnIdx = coinsMap.get("sha256")?.indexOf("BCHN", 0);
    if (bchnIdx && bchnIdx != -1) {
      coinsMap.get("sha256")?.splice(bchnIdx, 1);
      coinsMap.get("sha256")?.unshift("BCHN");
    }

    const btcIdx = coinsMap.get("sha256")?.indexOf("BTC", 0);
    if (btcIdx && btcIdx != -1) {
      coinsMap.get("sha256")?.splice(btcIdx, 1);
      coinsMap.get("sha256")?.unshift("BTC");
    }
  }

  if (coinsMap.has("scrypt")) {
    const dogeIdx = coinsMap.get("scrypt")?.indexOf("DOGE", 0);
    if (dogeIdx && dogeIdx != -1) {
      coinsMap.get("scrypt")?.splice(dogeIdx, 1);
      coinsMap.get("scrypt")?.unshift("DOGE");
    }

    const ltcIdx = coinsMap.get("scrypt")?.indexOf("LTC", 0);
    if (ltcIdx && ltcIdx != -1) {
      coinsMap.get("scrypt")?.splice(ltcIdx, 1);
      coinsMap.get("scrypt")?.unshift("LTC");
    }
  }
}

// ---------- main ----------

export function mapApiToDomainFoundBlocks(
  _currentLocale: string | undefined,
  foundBlocks: IFoundBlocks
): IDomainFoundBlock[] {
  const result: IDomainFoundBlock[] = [];

  const provideAge = (confirmations: number) => {
    if (confirmations == -1) return "ORPHAN";
    if (confirmations == 0) return "NEW";
    return String(confirmations);
  };

  foundBlocks.blocks.forEach((b) => {
    result.push({
      key: b.hash,
      height: b.height,
      hash: b.hash,
      confirmations: provideAge(b.confirmations),
      generatedCoins: b.generatedCoins,
      foundBy: b.foundBy,
      time: formatUnixTimeDateEnglish(b.time),
      timeOfDay: formatUnixTimeEnglish(b.time),
      dateLabel: formatUnixDateEnglish(b.time),
    });
  });
  return result;
}

export function mapToDomainPoolStatsHistory(
  poolStatsHistory: IPoolStatsHistory
): IDomainPoolStatHistories {
  const result: IDomainPoolStatHistory[] = [];
  const precision = getMaxHashratePoolStats(
    poolStatsHistory.stats,
    0,
    poolStatsHistory.powerMultLog10
  );
  const suffix =
    poolStatsHistory.powerUnit == AppConstants.PowerUnitHash
      ? getHashrateSuffix(precision)
      : AppConstants.PowerUnitCpd;

  let allZeros = true;
  poolStatsHistory.stats.forEach((s) => {
    const yValue = getHistoryYValue(
      poolStatsHistory.powerUnit,
      s.power,
      poolStatsHistory.powerMultLog10,
      precision
    );

    if (allZeros && yValue != 0) allZeros = false;

    result.push({
      x: new Date(s.time * 1000),
      y: yValue,
    });
  });
  return {
    suffix: suffix,
    histories: result,
    allZeros: allZeros,
  };
}

function getHistoryYValue(
  powerUnit: string,
  power: number,
  pow: number,
  precision: number
): number {
  if (powerUnit == AppConstants.PowerUnitHash) {
    return convertHashrateToFloatWithPrecision(power * Math.pow(10, pow), precision);
  }

  const value = power * Math.pow(10, pow);
  return value == 0
    ? 0
    : Number(
        value.toPrecision(precision > 0 ? precision : String(value).length)
      );
}

export function mapToDomainPoolStats(poolInfo: IPoolStats): IDomainPoolStat[] {
  const result: IDomainPoolStat[] = [];
  const firstStat = Array.isArray(poolInfo.stats) ? poolInfo.stats[0] : undefined;
  const powerUnit = firstStat?.powerUnit ?? AppConstants.PowerUnitHash;
  const power = firstStat?.power ?? 0;
  const powerMultLog10 = firstStat?.powerMultLog10 ?? 0;
  const clients = firstStat?.clients ?? 0;
  const workers = firstStat?.workers ?? 0;

  const provideHashrate = (
    unit: string,
    power: number,
    pow: number
  ): string => {
    if (unit == "hash")
      return convertHashrateToString(power * Math.pow(10, pow));

    const precise = String(power).length;
    const resultNumber = Number(power * Math.pow(10, pow)).toPrecision(precise);

    return `${resultNumber} ${unit}`;
  };

  result.push({
    title: "title-hashrate",
    value: provideHashrate(
      powerUnit,
      power,
      powerMultLog10
    ),
  });
  result.push({
    title: "title-clients",
    value: String(clients),
  });
  result.push({
    title: "title-workers",
    value: String(workers),
  });
  return result;
}

export function mapToDomainPoolLuck(luckInfo: IPoolLuck): IDomainPoolStat[] {
  const formatLuckValue = (value: number): string => {
    if (!Number.isFinite(value)) return "0.0000";
    return value.toFixed(4);
  };

  const luck = Array.isArray(luckInfo.luck) ? luckInfo.luck : [];
  const result: IDomainPoolStat[] = [];
  result.push({
    title: "title-period-day",
    value: formatLuckValue(luck[0]),
  });
  result.push({
    title: "title-period-days3",
    value: formatLuckValue(luck[1]),
  });
  result.push({
    title: "title-period-week",
    value: formatLuckValue(luck[2]),
  });
  result.push({
    title: "title-period-decade",
    value: formatLuckValue(luck[3]),
  });
  result.push({
    title: "title-period-month",
    value: formatLuckValue(luck[4]),
  });
  result.push({
    title: "title-period-quarter",
    value: formatLuckValue(luck[5]),
  });

  return result;
}

export function mapToDomainUserEnumerateAllAsMap(
  users: IUserData[]
): Map<string, IUserData> {
  const result: Map<string, IUserData> = new Map<string, IUserData>();
  users.forEach((u) => {
    result.set(u.login!, u);
  });

  const sorted = new Map(
    [...result.entries()].sort((a, b) => compareUsers(a[1], b[1]))
  );

  return sorted;
}

function compareUsers(first: IUserData, second: IUserData): number {
  if (first.power! > 0 || second.power! > 0)
    return second.power! - first.power!;
  return second.lastShareTime! - first.lastShareTime!;
}

// ---------- monitoring ---------
export function mapToDomainUserStats(userStats: IUserStats): IDomainPoolStat[] {
  const userStatsResult: IDomainPoolStat[] = [];
  userStatsResult.push({
    title: "title-hashrate",
    value: provideMonitoringHashrate(userStats.powerUnit, userStats.total.power, userStats.powerMultLog10),
  });
  userStatsResult.push({
    title: "title-workers",
    value: String(userStats.total.workers),
  });
  userStatsResult.push({
    title: "title-share-rate",
    value: formatShareRate(userStats.total.shareRate),
  });
  return userStatsResult;
}

export function mapToDomainWorkerStats(
  workers: IUserStatsWorker[],
  unit: string,
  toPow: number
): IDomainUserStatWorker[] {
  const result: IDomainUserStatWorker[] = [];

  const sortedWorkers = workers.sort(
    (a, b) => a.lastShareTime - b.lastShareTime
  );
  const currentTime = getCurrentTimeTimestamp();

  sortedWorkers.forEach((worker, idx) => {
    result.push({
      key: idx,
      worker: worker.name,
      sharerate: formatShareRateValue(worker.shareRate),
      hashrate: provideMonitoringHashrate(unit, worker.power, toPow),
      lastseen: worker.lastShareTime,
      workerState: calcWorkerState(currentTime, worker.lastShareTime),
    });
  });
  return result;
}

const provideMonitoringHashrate = (
  unit: string,
  power: number,
  pow: number
): string => {
  if (unit == "hash") return convertHashrateToString(power * Math.pow(10, pow));

  const precise = String(power).length;
  const resultNumber = Number(power * Math.pow(10, pow)).toPrecision(precise);

  return `${resultNumber} ${unit}`;
};

export function mapToDomainUserStatsHistory(
  userStatsHistory: IUserStatsHistory
): IDomainUserPoolStatHistories {
  const precision = getMaxHashrateUserStats(
    userStatsHistory.stats,
    0,
    userStatsHistory.powerMultLog10
  );
  const suffix =
    userStatsHistory.powerUnit == AppConstants.PowerUnitHash
      ? getHashrateSuffix(precision)
      : AppConstants.PowerUnitCpd;

  const result: IDomainUserPoolStatHistory[] = [];

  userStatsHistory.stats.forEach((s) => {
    const yValue = getHistoryYValue(
      userStatsHistory.powerUnit,
      s.power,
      userStatsHistory.powerMultLog10,
      precision
    );

    result.push({
      x: new Date(s.time * 1000),
      y: yValue,
    });
  });

  return {
    suffix: suffix,
    histories: result,
  };
}

export function mapToDomainUserBalance(
  coin: string,
  userBalance: IUserBalance
): IDomainUserBalance[] {
  const coinBalance = userBalance.balances.find((b) => b.coin == coin);
  const result: IDomainUserBalance[] = [];
  result.push({
    title: "title-monitoring-paid",
    value: coinBalance ? coinBalance.paid : "0",
  });
  result.push({
    title: "title-monitoring-requested",
    value: coinBalance ? coinBalance.requested : "0",
  });
  result.push({
    title: "title-monitoring-balance",
    value: coinBalance ? coinBalance.balance : "0",
  });
  result.push({
    title: "title-monitoring-queued",
    value: coinBalance ? coinBalance.queued : "0",
  });
  return result;
}

export function mapToDomainUserWorkerHistory(
  workerStatsHistory: IUserWorkerStatsHistory
): IDomainUserWorkerStatHistories {
  const precision = getMaxHashrateUserWorkerStats(
    workerStatsHistory.stats,
    workerStatsHistory.powerMultLog10
  );
  const suffix =
    workerStatsHistory.powerUnit == AppConstants.PowerUnitHash
      ? getHashrateSuffix(precision)
      : AppConstants.PowerUnitCpd;
  const result: IDomainUserWorkerStatHistory[] = [];

  workerStatsHistory.stats.forEach((s) => {
    const yValue = getHistoryYValue(
      workerStatsHistory.powerUnit,
      s.power,
      workerStatsHistory.powerMultLog10,
      precision
    );

    result.push({
      x: new Date(s.time * 1000),
      y: yValue,
    });
  });
  return {
    suffix: suffix,
    histories: result,
  };
}

export function mapToDomainUserStatsPoolHistory(
  userStatsHistory: IUserStatsHistory,
  _locale: string,
  historyAlgorithm?: string
): IDomainPoolHistoryUserStat[] {
  const result: IDomainPoolHistoryUserStat[] = [];

  userStatsHistory.stats.forEach((s, idx) => {
    const acceptedDifficulty = normalizeShareWorkToAcceptedDifficulty(
      s.shareWork,
      historyAlgorithm
    );
    if (s.shareRate == 0 && s.power == 0 && acceptedDifficulty == 0) return;

    result.unshift({
      key: idx,
      timeLabel: s.time,
      date: formatUnixDateEnglish(s.time - 86_400),
      shareRate: formatShareRate(s.shareRate),
      profitabilityUSD: "",
      profitabilityCoin: "",
      profitabilityCoin24h: "",
      profitabilityBTC: "",
      hashRate:
        userStatsHistory.powerUnit == AppConstants.PowerUnitHash
          ? convertHashrateToString(
              s.power * Math.pow(10, userStatsHistory.powerMultLog10)
            )
          : convertHashrateCpdString(s.power, userStatsHistory.powerMultLog10),
      difficulty: numberWithWhiteSpacesViaLocale(acceptedDifficulty),
    });
  });

  return result;
}

export function mapToDomainUserStatsPoolHistoryWiyhProfitability(
  userStatsHistory: IUserStatsHistory,
  pplnsResponse: IPPLNSAccResponse,
  locale: string,
  alg: string
): IDomainPoolHistoryUserStat[] {
  const payoutsByTime = new Map<
    number,
    { value: number; valueBTC: number; valueUSD: number }
  >();
  pplnsResponse.payouts.forEach((payout) => {
    payoutsByTime.set(payout.timeLabel, {
      value: Number(payout.value ?? 0),
      valueBTC: Number(payout.valueBTC ?? 0),
      valueUSD: Number(payout.valueUSD ?? 0),
    });
  });

  const profitabilityMultiplier =
    alg == "sha256" ? 100 : alg == "scrypt" ? 0.001 : 1;

  return mapToDomainUserStatsPoolHistoryWithCombinedProfitability(
    userStatsHistory,
    payoutsByTime,
    locale,
    profitabilityMultiplier,
    true,
    true,
    alg
  );
}

export function mapToDomainUserStatsPoolHistoryWithCombinedProfitability(
  userStatsHistory: IUserStatsHistory,
  payoutsByTime: Map<number, { value: number; valueBTC: number; valueUSD: number }>,
  _locale: string,
  profitabilityMultiplier: number,
  includeCoinProfitability = true,
  includeBtcProfitability = true,
  historyAlgorithm?: string
): IDomainPoolHistoryUserStat[] {
  const result: IDomainPoolHistoryUserStat[] = [];

  userStatsHistory.stats.forEach((s, idx) => {
    const acceptedDifficulty = normalizeShareWorkToAcceptedDifficulty(
      s.shareWork,
      historyAlgorithm
    );
    if (s.shareRate == 0 && s.power == 0 && acceptedDifficulty == 0) return;

    const payout = payoutsByTime.get(s.time);
    const payoutValue = payout ? Number(payout.value) : 0;
    const payoutValueBTC = payout ? Number(payout.valueBTC) : 0;
    const payoutValueUSD = payout ? Number(payout.valueUSD) : 0;

    const hasPayoutData =
      payout != undefined && (payoutValue != 0 || payoutValueUSD != 0 || payoutValueBTC != 0);
    const profitabilityUSD = hasPayoutData
      ? calcProfitability(
          s.power,
          userStatsHistory.powerMultLog10,
          payoutValueUSD,
          profitabilityMultiplier
        )
      : "No data";
    const profitabilityCoin = includeCoinProfitability
      ? hasPayoutData
        ? calcProfitability(
            s.power,
            userStatsHistory.powerMultLog10,
            payoutValue,
            profitabilityMultiplier
          )
        : "No data"
      : "";
    const profitabilityCoin24h = includeCoinProfitability
      ? hasPayoutData
        ? String(payoutValue)
        : "No data"
      : "";
    const profitabilityBTC = includeBtcProfitability
      ? hasPayoutData
        ? calcProfitability(
            s.power,
            userStatsHistory.powerMultLog10,
            payoutValueBTC,
            profitabilityMultiplier
          )
        : "No data"
      : "";

    result.unshift({
      key: idx,
      timeLabel: s.time,
      date: formatUnixDateEnglish(s.time - 86_400),
      shareRate: formatShareRate(s.shareRate),
      profitabilityUSD: profitabilityUSD,
      profitabilityCoin: profitabilityCoin,
      profitabilityCoin24h: profitabilityCoin24h,
      profitabilityBTC: profitabilityBTC,
      hashRate:
        userStatsHistory.powerUnit == AppConstants.PowerUnitHash
          ? convertHashrateToString(
              s.power * Math.pow(10, userStatsHistory.powerMultLog10)
            )
          : convertHashrateCpdString(s.power, userStatsHistory.powerMultLog10),
      difficulty: numberWithWhiteSpacesViaLocale(acceptedDifficulty),
    });
  });

  return result;
}

function calcProfitability(
  power: number,
  pow: number,
  payout: number,
  multiplier: number
): string {
  const baseHashrate = power / Math.pow(10, Math.abs(pow));
  if (!Number.isFinite(baseHashrate) || baseHashrate <= 0) return "No data";
  const value = (payout / baseHashrate) * multiplier;
  return String(value);
}

function convertHashrateCpdString(power: number, pow: number): string {
  const value = power * Math.pow(10, pow);
  // return `${String(value)} cpd`;
  return `${String(value.toFixed(3))} cpd`;
}

export function mapToDomainPoolHistory(
  poolHistory: IPoolHistory,
  _locale: string,
  historyAlgorithm?: string
): IDomainPoolHistoryStat[] {
  const result: IDomainPoolHistoryStat[] = [];

  // const currentTimeStamp = getCurrentTimeTimestamp();

  poolHistory.stats.forEach((s, idx) => {
    const acceptedDifficulty = normalizeShareWorkToAcceptedDifficulty(
      s.shareWork,
      historyAlgorithm
    );
    if (s.shareRate == 0 && s.power == 0 && acceptedDifficulty == 0) return;
    // if (s.time > currentTimeStamp) return;

    result.unshift({
      key: idx,
      date: formatUnixDateEnglish(s.time - 86_400),
      shareRate: formatShareRate(s.shareRate),
      hashRate:
        poolHistory.powerUnit == AppConstants.PowerUnitHash
          ? convertHashrateToString(
              s.power * Math.pow(10, poolHistory.powerMultLog10)
            )
          : convertHashrateCpdString(s.power, poolHistory.powerMultLog10),
      difficulty: numberWithWhiteSpacesViaLocale(acceptedDifficulty),
    });
  });

  return result;
}

function normalizeShareWorkToAcceptedDifficulty(
  shareWork: number,
  historyAlgorithm?: string
): number {
  const divisor = isScryptAlgorithm(historyAlgorithm)
    ? SHARE_WORK_NORMALIZATION_DIVISOR_SCRYPT
    : SHARE_WORK_NORMALIZATION_DIVISOR_DEFAULT;
  const normalized = Number(shareWork) / divisor;
  if (!Number.isFinite(normalized) || normalized <= 0) return 0;
  return Math.round(normalized);
}

function isScryptAlgorithm(historyAlgorithm?: string): boolean {
  return String(historyAlgorithm ?? "").toLowerCase() == "scrypt";
}

// export function mapToDomainConnectInfo(
//   coins: string[],
//   response: IConnect
// ): Map<string, IDomainConnect[]> {
//   const result: Map<string, IDomainConnect[]> = new Map<
//     string,
//     IDomainConnect[]
//   >();
// 
//   let counter = 0;
// 
//   coins.forEach((coin) => {
//     result.set(coin, []);
//     response.instances.forEach((instance) => {
//       if (instance.backends.includes(coin)) {
//         result.get(coin)!.push({
//           key: counter,
//           protocol: instance.protocol,
//           type: instance.type,
//           port: String(instance.port),
//           backends: instance.backends.join(", "),
//           difficulty:
//             instance.shareDiff != undefined
//               ? numberWithWhiteSpaces(instance.shareDiff)
//               : "0",
//         });
//         counter++;
//       }
//     });
//   });
// 
//   return result;
// }

export function mapToDomainConnectInfo(
  coins: string[],
  response: IConnect
): Map<string, IDomainConnect[]> {
  const resultArray: [string, IDomainConnect[]][] = [];
  let counter = 0;

  coins.forEach((coin) => {
    const connects: IDomainConnect[] = [];

    response.instances.forEach((instance) => {
      if (instance.backends.includes(coin)) {
        connects.push({
          key: counter,
          protocol: instance.protocol,
          type: instance.type,
          port: String(instance.port),
          backends: instance.backends.join(", "),
          difficulty:
            instance.shareDiff !== undefined
              ? numberWithWhiteSpaces(instance.shareDiff)
              : "0",
        });
        counter++;
      }
    });

    resultArray.push([coin, connects]);
  });

  return new Map(resultArray);
}


export function mapToDomainPayoutInfo(
  response: IPayouts
): IDomainPayout[] {
  const result: IDomainPayout[] = [];

  response.payouts.forEach((p) => {
    if (p.value != "" && p.txid != "") {
      result.push({
        key: p.txid,
        date: normalizeUnixTimeToSeconds(p.time),
        txid: p.txid,
        amount: formatPayoutCoinAmount(p.value),
        amountBTC: String(p.valueBTC ?? ""),
        amountUSD: String(p.valueUSD ?? ""),
        status: p.status,
      });
    }
  });

  return result;
}

function normalizeUnixTimeToSeconds(rawTime: number): number {
  const numeric = Number(rawTime);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.floor(numeric / 1000);
}

function formatPayoutCoinAmount(rawValue: string): string {
  const normalized = String(rawValue ?? "")
    .trim()
    .replace(/\s+/g, "")
    .replace(",", ".");
  if (normalized.length == 0) return "0";

  const sign = normalized.startsWith("-")
    ? "-"
    : normalized.startsWith("+")
      ? "+"
      : "";
  const unsigned = sign.length > 0 ? normalized.slice(1) : normalized;
  const [integerPartRaw, fractionalPartRaw = ""] = unsigned.split(".");
  const integerDigitsRaw = integerPartRaw.replace(/\D/g, "");
  const fractionalDigits = fractionalPartRaw.replace(/\D/g, "");
  const integerDigitsNormalized = (
    integerDigitsRaw.length > 0 ? integerDigitsRaw : "0"
  ).replace(/^0+(?=\d)/, "");
  const integerPartGrouped = integerDigitsNormalized.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    " "
  );

  if (fractionalDigits.length == 0) {
    return `${sign}${integerPartGrouped}`;
  }
  if (fractionalDigits.length <= 2) {
    return `${sign}${integerPartGrouped}.${fractionalDigits}`;
  }

  const leadingFraction = fractionalDigits.slice(0, 2);
  let tailFraction = fractionalDigits.slice(2);
  const remainder = tailFraction.length % 3;
  if (remainder != 0) {
    tailFraction = `${tailFraction}${"0".repeat(3 - remainder)}`;
  }
  const tailGrouped = tailFraction.match(/.{1,3}/g)?.join(" ") ?? "";

  return tailGrouped.length > 0
    ? `${sign}${integerPartGrouped}.${leadingFraction} ${tailGrouped}`
    : `${sign}${integerPartGrouped}.${leadingFraction}`;
}

export function mapToDomainUserSettings(
  response: IUserSettings
): Map<string, IUserSettingsCoin> {
  return mapToDomainUserSettingsCoins(response.coins);
}

export function mapToDomainUserSettingsCoins(
  coins: IUserSettingsCoin[] | undefined
): Map<string, IUserSettingsCoin> {
  const result: Map<string, IUserSettingsCoin> = new Map();

  if (coins != undefined) {
    coins.forEach((c) => {
      result.set(c.name, c);
    });
  }

  return result;
}

export function mapToDomainProfitSettings(
  coins: ICoinRatio[]
): IDomainCoinRatio[] {
  const result: IDomainCoinRatio[] = [];
  coins.forEach((c) => {
    result.push({
      coin: c.name,
      ratio: c.profitSwitchCoeff,
    });
  });
  return result;
}

// export function mapToDomainUsers(
//   users: Map<string, IUserData>,
//   selectedCoin: string | undefined
// ): IDomainUser[] {
//   const result: IDomainUser[] = [];
// 
//   const unit = selectedCoin != "XPM" ? AppConstants.PowerUnitHash : AppConstants.PowerUnitCpd;
//   const pow = selectedCoin != "XPM" ? AppConstants.PowerHash : AppConstants.PowerCPD;
// 
//   users.values().forEach((u) => {
//     result.push({
//       key: u.login,
//       loginAndName: `${u.login}\u00A0 | \u00A0${u.name}`,
//       login: u.login ?? "",
//       publicName: u.name ?? "",
//       email: u.email ?? "",
//       registrationDate: u.registrationDate ?? 0,
//       feePlanId: u.feePlanId,
//       power: u.power ?? 0,
//       powerSimple: provideMonitoringHashrate(unit, u.power ?? 0, pow),
//       lastSeen: u.lastShareTime ?? 0,
//       isReadOnly: u.isReadOnly ?? false,
//       workers: u.workers ?? 0,
//       shareRate: u.shareRate ?? 0,
//     });
//   });
// 
//   return result;
// }

export function mapToDomainUsers(
  users: Map<string, IUserData>,
  selectedCoin: string | undefined,
  selectedAlgorithm?: string | undefined
): IDomainUser[] {
  const result: IDomainUser[] = [];

  const normalizedCoin = String(selectedCoin ?? "").trim().toUpperCase();
  const normalizedAlgorithm = String(selectedAlgorithm ?? "").trim().toLowerCase();
  const unit =
    normalizedCoin == "XPM"
      ? AppConstants.PowerUnitCpd
      : AppConstants.PowerUnitHash;
  const pow =
    normalizedCoin == "XPM"
      ? AppConstants.PowerCPD
      : normalizedAlgorithm == "scrypt" || normalizedCoin == "SCRYPT"
      ? 3
      : AppConstants.PowerHash;

  Array.from(users.values()).forEach((u) => {
    result.push({
      key: u.login,
      loginAndName: `${u.login}\u00A0 | \u00A0${u.name}`,
      login: u.login ?? "",
      publicName: u.name ?? "",
      email: u.email ?? "",
      registrationDate: u.registrationDate ?? 0,
      feePlanId: u.feePlanId,
      power: u.power ?? 0,
      powerSimple: provideMonitoringHashrate(unit, u.power ?? 0, pow),
      lastSeen: u.lastShareTime ?? 0,
      isReadOnly: u.isReadOnly ?? false,
      workers: u.workers ?? 0,
      shareRate: u.shareRate ?? 0,
    });
  });

  return result;
}


export function mapToDomainFeePlansAsArray(response: IFeePlans): string[] {
  const result: string[] = [];
  response.plans.forEach((p) => {
    result.push(p.feePlanId);
  });
  return result;
}

function calcWorkerState(
  currentTime: number,
  workerLastShareTime: number
): "Normal" | "Error" | "Warning" {
  if (currentTime - workerLastShareTime > 1800) return "Error"; //30m
  else if (currentTime - workerLastShareTime > 900) return "Warning"; //15m
  return "Normal";
}
